/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PublishIcon from "@mui/icons-material/Publish";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import Tooltip from "@mui/material/Tooltip";
import { Pagination, WarnModal } from "../../components";
import {
  Loader,
  showError,
  showToast,
  showWarning,
  STORAGE_KEYS,
  getFromStorage,
  EXCERPT_MAX_CHARS,
  getExcerptLength,
} from "../../constants";
import { usePersistedState } from "../../hooks";
import { Post } from "../../types/General";
import { isValidInput } from "../../utils/validations";
import {
  DeletePost,
  DuplicatePost,
  GenerateAvatarVideo,
  GetCategories,
  GetPostById,
  GetPosts,
  UpdatePostStatus,
} from "../../services/home";

// avatarVideo.status values that mean a generation is actively in flight
const VIDEO_IN_PROGRESS = ["queued", "kling_processing", "downloading", "captioning"];

interface Category {
  _id: string;
  name: string;
  description?: string;
  parentCategoryId?: string | null;
}

const ManagePosts = () => {
  const navigate = useNavigate();

  // Data
  const [posts, setPosts] = useState<Post[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = usePersistedState("posts.page", 1);

  // Filters
  const [searchTerm, setSearchTerm] = usePersistedState("posts.search", "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = usePersistedState("posts.debouncedSearch", "");
  const [contentType, setContentType] = usePersistedState("posts.contentType", "");
  const [postStatus, setPostStatus] = usePersistedState("posts.status", "");
  const [selectedParentId, setSelectedParentId] = usePersistedState("posts.parentCat", "");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = usePersistedState("posts.subCat", "");

  // Actions menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // Add post menu
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  // Discard modal
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardId, setDiscardId] = useState("");

  // Generate-video confirmation modal
  const [videoConfirmOpen, setVideoConfirmOpen] = useState(false);
  const [videoGenerateId, setVideoGenerateId] = useState("");

  // Copy link
  const [copyingId, setCopyingId] = useState<string | null>(null);

  const totalPages = Math.ceil(totalCount / 10);

  // Derived category lists
  const parentCategories = useMemo(
    () => categories.filter((c) => !c.parentCategoryId),
    [categories]
  );

  const subcategories = useMemo(
    () =>
      selectedParentId
        ? categories.filter((c) => c.parentCategoryId === selectedParentId)
        : [],
    [categories, selectedParentId]
  );

  // Determine which categoryId to send to the API
  const activeCategoryId = selectedSubcategoryId || selectedParentId || "";

  // --- Data fetching ---

  const fetchCategories = async () => {
    try {
      const response = (await GetCategories()) as {
        code: number;
        data: Category[];
      };
      if (response?.code === 200) {
        setCategories(response.data || []);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch categories");
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = (await GetPosts({
        limit: 10,
        skip: (page - 1) * 10,
        search: debouncedSearchTerm,
        contentType,
        status: postStatus,
        categoryId: activeCategoryId || undefined,
      })) as { status: number; count: number; data: Post[] };

      if (response?.status === 200) {
        setPosts(response.data || []);
        setTotalCount(response.count || 0);
      } else {
        setPosts([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch posts");
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 1000);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  useEffect(() => {
    fetchPosts();
  }, [debouncedSearchTerm, page, contentType, postStatus, activeCategoryId]);

  // --- Handlers ---

  const onPageChange = (newPage: number) => setPage(newPage);

  const handleParentChange = (parentId: string) => {
    setSelectedParentId(parentId);
    setSelectedSubcategoryId("");
    setPage(1);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleStatusChange = async (status: string, id: string) => {
    try {
      setLoading(true);
      const response = (await UpdatePostStatus(id, { status })) as {
        status: number;
      };
      if (response?.status === 200 || response?.status === 201) {
        showToast("Status updated successfully");
        setPosts((prev) =>
          prev.map((p) =>
            p._id === id ? { ...p, status: status as Post["status"] } : p
          )
        );
      }
    } catch (error) {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = async () => {
    try {
      if (!discardId) {
        showError("Please try again");
        return;
      }
      setLoading(true);
      const response = (await DeletePost(discardId)) as { status: number };
      if (response?.status === 200) {
        showToast("Post deleted successfully");
        fetchPosts();
      }
    } catch (error) {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (postId: string) => {
    try {
      setLoading(true);
      const response = (await DuplicatePost(postId)) as {
        status: number;
        data?: { _id: string; contentType: string; categoryId: string };
      };
      if (response?.status === 201 && response.data) {
        const basePath =
          response.data.contentType === "Video"
            ? "/manage-videos"
            : "/manage-articles";
        navigate(
          `${basePath}/${response.data.categoryId}/edit/${response.data._id}`
        );
      }
    } catch (error) {
      showError("Failed to duplicate post");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: any) => {
    if (!categoryId) return "—";
    // Handle populated object (if API returns { _id, name })
    if (typeof categoryId === "object" && categoryId?.name) return categoryId.name;
    // Handle string ID — look up in categories list
    return categories.find((c) => c._id === categoryId)?.name || "—";
  };

  const truncateTitle = (title: string | undefined | null, wordLimit = 6) => {
    if (!title) return "Untitled";
    const words = title.split(" ");
    if (words.length <= wordLimit) return title;
    return words.slice(0, wordLimit).join(" ") + "...";
  };

  const handleCopyTitle = async (item: Post) => {
    setCopyingId(item._id);
    try {
      const response = await fetch("https://api2.branch.io/v1/url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          branch_key: "key_live_fuoBZoZEWSgxwRalduDOuaantFg35jbD",
          data: {
            $canonical_identifier: item._id,
            contentType: item.contentType,
            title: item.title,
            pageTitle: item.title,
            linkType: "shareArticle",
            $uri_redirect_mode: "1",
          },
        }),
      });
      const json = await response.json();
      if (!json.url) throw new Error(json.message ?? "No URL returned");
      await navigator.clipboard.writeText(`${item.title}\n${json.url}`);
      showToast("Copied to clipboard");
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : "Failed to generate link");
    } finally {
      setCopyingId(null);
    }
  };

  const handleDownloadAudio = async (item: Post) => {
    try {
      const token = getFromStorage(STORAGE_KEYS.token);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/post/${item._id}/download-audio`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.title}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showError("Failed to download audio");
    }
  };

  const handleGenerateVideo = async (postId: string) => {
    if (!postId) return;
    try {
      const response = (await GenerateAvatarVideo(postId)) as { status: number };
      if (response?.status === 200) {
        showToast("Video generation started");
        fetchPosts();
      } else if (response?.status === 409) {
        showError("Video generation is already in progress");
      } else {
        showError("Failed to start video generation");
      }
    } catch {
      showError("Failed to start video generation");
    }
  };

  // Pre-generation guard: the avatar video needs ≤ ~30s of speech, i.e. an
  // executive summary of EXCERPT_MAX_CHARS visible characters or fewer. If the
  // post is over, send the user to the editor (where the counter shows the
  // overage) instead of opening the cost modal.
  const handleGenerateVideoClick = async (item: Post | null) => {
    if (!item?._id) return;
    const { _id, categoryId, contentType } = item;
    handleMenuClose();
    try {
      const res = (await GetPostById({ postId: _id })) as { data?: Post };
      if (getExcerptLength(res?.data?.excerpts ?? "") > EXCERPT_MAX_CHARS) {
        showWarning(
          `Reduce the executive summary to ${EXCERPT_MAX_CHARS} characters or less to generate the avatar video.`
        );
        const basePath =
          contentType === "Video" ? "/manage-videos" : "/manage-articles";
        navigate(`${basePath}/${categoryId}/edit/${_id}`);
        return;
      }
      setVideoGenerateId(_id);
      setVideoConfirmOpen(true);
    } catch {
      showError("Failed to start video generation");
    }
  };

  const handleDownloadVideo = async (item: Post) => {
    try {
      const token = getFromStorage(STORAGE_KEYS.token);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/post/${item._id}/download-video`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.title}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showError("Failed to download video");
    }
  };

  // While any visible post has a video generating, refresh the list so the
  // status badge advances live. Pauses when the tab is hidden; stops when
  // nothing is in progress.
  const hasInProgressVideo = useMemo(
    () => posts.some((p) => VIDEO_IN_PROGRESS.includes(p.avatarVideo?.status ?? "")),
    [posts]
  );

  useEffect(() => {
    if (!hasInProgressVideo) return;
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") fetchPosts();
    }, 12000);
    return () => clearInterval(intervalId);
  }, [hasInProgressVideo]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          <div className="heading-bread">
            <h1 className="mn_hdng">Posts</h1>
          </div>
        </div>

        <Card className="cards">
          <Box className="cards_header">
            <Grid
              container
              spacing={2}
              sx={{ mb: 1 }}
              alignItems="center"
              justifyContent="space-between"
            >
              {/* Left: Search */}
              <Grid item xs={12} md="auto">
                <TextField
                  placeholder="Search by title or author"
                  value={searchTerm}
                  size="small"
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      onPageChange(1);
                      setSearchTerm(e.target.value);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: "100%", md: 280 } }}
                />
              </Grid>

              {/* Right: Filters + Add */}
              <Grid item xs={12} md="auto">
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                  }}
                >
                  {/* Content Type Filter */}
                  <FormControl
                    size="small"
                    sx={{ minWidth: 120, display: { xs: "none", md: "inline-flex" } }}
                  >
                    <Select
                      value={contentType}
                      onChange={(e) => {
                        setContentType(e.target.value);
                        setPage(1);
                      }}
                      displayEmpty
                      sx={{ borderRadius: "8px", fontSize: "13px" }}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="Article">Article</MenuItem>
                      <MenuItem value="Video">Video</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Parent Category Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={selectedParentId}
                      onChange={(e) => handleParentChange(e.target.value)}
                      displayEmpty
                      sx={{ borderRadius: "8px", fontSize: "13px" }}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {parentCategories.map((cat) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Subcategory Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={selectedSubcategoryId}
                      onChange={(e) => {
                        setSelectedSubcategoryId(e.target.value);
                        setPage(1);
                      }}
                      displayEmpty
                      disabled={!selectedParentId}
                      sx={{ borderRadius: "8px", fontSize: "13px" }}
                    >
                      <MenuItem value="">All Subcategories</MenuItem>
                      {subcategories.map((cat) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Status Filter */}
                  <FormControl
                    size="small"
                    sx={{ minWidth: 120, display: { xs: "none", md: "inline-flex" } }}
                  >
                    <Select
                      value={postStatus}
                      onChange={(e) => {
                        setPostStatus(e.target.value);
                        setPage(1);
                      }}
                      displayEmpty
                      sx={{ borderRadius: "8px", fontSize: "13px" }}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="Published">Published</MenuItem>
                      <MenuItem value="Received">Received</MenuItem>
                      <MenuItem value="Draft">Draft</MenuItem>
                      <MenuItem value="Disabled">Disabled</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Add Post Button with Dropdown */}
                  <Button
                    variant="contained"
                    sx={{ height: 40 }}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                  >
                    Add Post
                  </Button>
                  <Menu
                    anchorEl={addMenuAnchor}
                    open={Boolean(addMenuAnchor)}
                    onClose={() => setAddMenuAnchor(null)}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 160,
                        borderRadius: "10px",
                        boxShadow:
                          "0px 2px 8px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        navigate("/manage-articles/add/add");
                        setAddMenuAnchor(null);
                      }}
                    >
                      Add Article
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        navigate("/manage-videos/add");
                        setAddMenuAnchor(null);
                      }}
                    >
                      Add Video
                    </MenuItem>
                  </Menu>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table */}
          <TableContainer className="table_container">
            <Table sx={{ minWidth: 650 }} aria-label="posts table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">TITLE</TableCell>
                  <TableCell align="center">TYPE</TableCell>
                  <TableCell align="center">AUTHOR</TableCell>
                  <TableCell align="center">CATEGORY</TableCell>
                  <TableCell align="center">STATUS</TableCell>
                  <TableCell align="center">POSTED</TableCell>
                  <TableCell align="center">COMMENTS</TableCell>
                  <TableCell align="center">ACTIONS</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {posts.length ? (
                  posts.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell align="left">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {truncateTitle(item.title)}
                          <Tooltip title="Copy title & app link">
                            <span>
                              <IconButton
                                size="small"
                                disabled={copyingId === item._id}
                                onClick={() => handleCopyTitle(item)}
                                sx={{
                                  padding: "2px",
                                  color: "#94a3b8",
                                  "&:hover": { color: "#475569" },
                                }}
                              >
                                {copyingId === item._id ? (
                                  <CircularProgress size={12} sx={{ color: "#94a3b8" }} />
                                ) : (
                                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {item.excerptsAudio && (
                            <Tooltip title="Download audio">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadAudio(item)}
                                sx={{
                                  padding: "2px",
                                  color: "#94a3b8",
                                  "&:hover": { color: "#475569" },
                                }}
                              >
                                <VolumeUpOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {VIDEO_IN_PROGRESS.includes(
                            item.avatarVideo?.status ?? ""
                          ) && (
                            <Tooltip
                              title={
                                item.avatarVideo?.status === "captioning"
                                  ? "Rendering captions…"
                                  : "Generating avatar video…"
                              }
                            >
                              <Box sx={{ display: "inline-flex", padding: "2px" }}>
                                <CircularProgress
                                  size={14}
                                  sx={{ color: "#6366f1" }}
                                />
                              </Box>
                            </Tooltip>
                          )}
                          {item.avatarVideo?.status === "completed" && (
                            <Tooltip title="Download video">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadVideo(item)}
                                sx={{
                                  padding: "2px",
                                  color: "#16a34a",
                                  "&:hover": { color: "#15803d" },
                                }}
                              >
                                <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {item.avatarVideo?.status === "failed" && (
                            <Tooltip
                              title={
                                item.avatarVideo?.error ||
                                "Video generation failed"
                              }
                            >
                              <Box sx={{ display: "inline-flex", padding: "2px" }}>
                                <ErrorOutlineIcon
                                  sx={{ fontSize: 16, color: "#ef4444" }}
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.contentType}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            backgroundColor:
                              item.contentType === "Article"
                                ? "#dbeafe"
                                : "#fae8ff",
                            color:
                              item.contentType === "Article"
                                ? "#2563eb"
                                : "#a21caf",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {item.author?.length
                          ? item.author.replace("JustIdt", "J the App").replace("JustIDT", "J the App")
                          : "N/A"}
                      </TableCell>
                      <TableCell align="center">
                        {getCategoryName(item.categoryId)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            backgroundColor:
                              item.status === "Published"
                                ? "#dcfce7"
                                : item.status === "Draft"
                                  ? "#fef9c3"
                                  : item.status === "Disabled"
                                    ? "#fee2e2"
                                    : item.status === "Received"
                                      ? "#e0e7ff"
                                      : "#f1f5f9",
                            color:
                              item.status === "Published"
                                ? "#16a34a"
                                : item.status === "Draft"
                                  ? "#ca8a04"
                                  : item.status === "Disabled"
                                    ? "#dc2626"
                                    : item.status === "Received"
                                      ? "#4f46e5"
                                      : "#64748b",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {moment(item.createdAt).format("MMM DD, YYYY")}
                      </TableCell>
                      <TableCell align="center">
                        {item.commentCount || 0}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, item)}
                          sx={{
                            backgroundColor: "#f1f5f9",
                            "&:hover": { backgroundColor: "#e2e8f0" },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={8}
                      sx={{ color: "#051140" }}
                    >
                      No Posts Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Pagination
          page={page}
          onPageChange={onPageChange}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      </div>

      {/* Row Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
            mt: 1,
            minWidth: 160,
            borderRadius: "10px",
            "& .MuiMenuItem-root": {
              padding: "10px 16px",
              fontSize: "13px",
              fontWeight: 500,
              gap: "10px",
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            const basePath =
              selectedItem?.contentType === "Video"
                ? "/manage-videos"
                : "/manage-articles";
            navigate(
              `${basePath}/${selectedItem?.categoryId}/edit/${selectedItem?._id}`
            );
            handleMenuClose();
          }}
          onMouseDown={(e: React.MouseEvent) => {
            if (e.button === 1 || (e.button === 0 && (e.ctrlKey || e.metaKey))) {
              e.preventDefault();
              const basePath =
                selectedItem?.contentType === "Video"
                  ? "/manage-videos"
                  : "/manage-articles";
              window.open(
                `${basePath}/${selectedItem?.categoryId}/edit/${selectedItem?._id}`,
                "_blank"
              );
              handleMenuClose();
            }
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: "#64748b" }} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleDuplicate(selectedItem?._id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" sx={{ color: "#64748b" }} />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>

        {selectedItem?.status === "Published" &&
          selectedItem?.excerptsAudio &&
          !VIDEO_IN_PROGRESS.includes(
            selectedItem?.avatarVideo?.status ?? ""
          ) && (
            <MenuItem onClick={() => handleGenerateVideoClick(selectedItem)}>
              <ListItemIcon>
                <MovieCreationOutlinedIcon
                  fontSize="small"
                  sx={{ color: "#6366f1" }}
                />
              </ListItemIcon>
              <ListItemText>
                {selectedItem?.avatarVideo?.status === "completed" ||
                selectedItem?.avatarVideo?.status === "failed"
                  ? "Regenerate Video"
                  : "Generate Video"}
              </ListItemText>
            </MenuItem>
          )}

        {(selectedItem?.status === "Draft" ||
          selectedItem?.status === "Received") && (
          <MenuItem
            onClick={() => {
              setDiscardId(selectedItem?._id);
              setDiscardOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: "#ef4444" }} />
            </ListItemIcon>
            <ListItemText>Discard</ListItemText>
          </MenuItem>
        )}

        {selectedItem?.status === "Published" && (
          <MenuItem
            onClick={() => {
              handleStatusChange("Disabled", selectedItem?._id);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <VisibilityOffIcon fontSize="small" sx={{ color: "#f59e0b" }} />
            </ListItemIcon>
            <ListItemText>Disable</ListItemText>
          </MenuItem>
        )}

        {selectedItem?.status === "Disabled" && (
          <MenuItem
            onClick={() => {
              handleStatusChange("Published", selectedItem?._id);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <PublishIcon fontSize="small" sx={{ color: "#22c55e" }} />
            </ListItemIcon>
            <ListItemText>Publish</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <WarnModal
        open={discardOpen}
        setOpen={setDiscardOpen}
        handleDelete={handleDiscard}
        name="Post"
      />

      <WarnModal
        open={videoConfirmOpen}
        setOpen={setVideoConfirmOpen}
        handleDelete={() => handleGenerateVideo(videoGenerateId)}
        name="Video"
        title="Generate avatar video?"
        description="This creates a captioned avatar video from the executive-summary using our partner's API credits. Continue?"
        confirmLabel="Generate"
        confirmColor="primary"
      />
    </MainContainer>
  );
};

export default ManagePosts;
